import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Editor } from "@tiptap/react";
import {
  BsTypeBold,
  BsTypeItalic,
  BsTypeUnderline,
  BsListOl,
  BsListUl,
  BsTextLeft,
  BsTextCenter,
  BsTextRight,
  BsJustify,
  BsArrowCounterclockwise,
  BsArrowClockwise,
  BsImage,
  BsTable,
  BsCode,
  BsBlockquoteLeft,
  BsHighlighter,
  BsPlusLg,
  BsChatDots,
  BsFileDiff,
  BsLink45Deg,
  BsTextIndentLeft,
  BsTextIndentRight,
  BsDistributeVertical,
  BsSearch,
  BsThreeDotsVertical,
  BsChevronLeft,
  BsChevronRight,
  BsCheck,
  BsX,
} from "react-icons/bs";
import {
  LuHeading1,
  LuHeading2,
  LuHeading3,
  LuHeading4,
  LuHeading5,
  LuHeading6,
} from "react-icons/lu";
import { VscHorizontalRule } from "react-icons/vsc";
// FaRegComment removed - Add Comment moved to CommentsPanel
import { TbMathFunction } from "react-icons/tb";
import { FiSave } from "react-icons/fi";
import { AiOutlineFontColors } from "react-icons/ai";
import { RiAlignLeft } from "react-icons/ri";
import MathInputModal from "./MathInputModal";
import {
  FONT_FAMILIES,
  resolveDropdownFont,
  isPresetFontValue,
  primaryFontName,
} from "./utils/fontMap";
import { TrackChangeInfo } from "./extensions/TrackChangesExtension";

/**
 * Render the font dropdown's options. When the current value is not a
 * preset (unknown imported font like "Aptos" or "宋体"), prepend a dynamic
 * option for it — a controlled <select> whose value matches no option
 * silently displays the FIRST option ("Times New Roman"), lying about the
 * font AND making "re-select Times New Roman" a dead click (no change
 * event fires).
 */
function renderFontOptions(currentValue: string) {
  return (
    <>
      {!isPresetFontValue(currentValue) && (
        <option value={currentValue}>{primaryFontName(currentValue)}</option>
      )}
      {FONT_FAMILIES.map(font => (
        <option key={font.value} value={font.value}>
          {font.label}
        </option>
      ))}
    </>
  );
}

/** Same dynamic-option treatment for off-list sizes (e.g. "10.5pt"). */
function renderFontSizeOptions(currentValue: string) {
  return (
    <>
      {currentValue && !FONT_SIZES.some(s => s.value === currentValue) && (
        <option value={currentValue}>{currentValue}</option>
      )}
      {FONT_SIZES.map(size => (
        <option key={size.value} value={size.value}>
          {size.label}
        </option>
      ))}
    </>
  );
}

const FONT_SIZES = [
  { label: "12pt", value: "" },
  { label: "8pt", value: "8pt" },
  { label: "9pt", value: "9pt" },
  { label: "10pt", value: "10pt" },
  { label: "11pt", value: "11pt" },
  { label: "12pt", value: "12pt" },
  { label: "14pt", value: "14pt" },
  { label: "16pt", value: "16pt" },
  { label: "18pt", value: "18pt" },
  { label: "20pt", value: "20pt" },
  { label: "24pt", value: "24pt" },
  { label: "28pt", value: "28pt" },
  { label: "32pt", value: "32pt" },
  { label: "36pt", value: "36pt" },
  { label: "48pt", value: "48pt" },
];

const TEXT_COLORS = [
  { label: "Default", value: "" },
  { label: "Black", value: "#000000" },
  { label: "Dark Gray", value: "#4a4a4a" },
  { label: "Gray", value: "#9b9b9b" },
  { label: "Red", value: "#e53935" },
  { label: "Orange", value: "#fb8c00" },
  { label: "Yellow", value: "#fdd835" },
  { label: "Green", value: "#43a047" },
  { label: "Teal", value: "#00897b" },
  { label: "Blue", value: "#1e88e5" },
  { label: "Indigo", value: "#3949ab" },
  { label: "Purple", value: "#8e24aa" },
  { label: "Pink", value: "#d81b60" },
];

const HIGHLIGHT_COLORS = [
  { label: "None", value: "" },
  { label: "Yellow", value: "#ffff00" },
  { label: "Lime", value: "#ccff00" },
  { label: "Green", value: "#00ff00" },
  { label: "Cyan", value: "#00ffff" },
  { label: "Sky Blue", value: "#00ccff" },
  { label: "Blue", value: "#99ccff" },
  { label: "Purple", value: "#cc99ff" },
  { label: "Pink", value: "#ff99cc" },
  { label: "Red", value: "#ff9999" },
  { label: "Orange", value: "#ffcc99" },
  { label: "Peach", value: "#ffcc66" },
];

interface EditorToolbarProps {
  editor: Editor;
  onExportDocx?: () => void;
  onImportDocx?: (file: File) => void;
  // Save
  onSave?: () => void;
  isSaving?: boolean;
  showUnsavedBadge?: boolean;
  // Track changes
  trackChangesEnabled?: boolean;
  onToggleTrackChanges?: () => void;
  trackChanges?: TrackChangeInfo[];
  currentChangeIndex?: number;
  onNavigateChange?: (direction: "prev" | "next") => void;
  onAcceptChange?: (changeId: string) => void;
  onRejectChange?: (changeId: string) => void;
  onAcceptAllChanges?: () => void;
  onRejectAllChanges?: () => void;
  // Comments panel
  showCommentsPanel?: boolean;
  onToggleCommentsPanel?: () => void;
  commentsCount?: number;
  onAddComment?: () => void;
  // Track changes panel
  showTrackChangesPanel?: boolean;
  onToggleTrackChangesPanel?: () => void;
  trackChangesCount?: number;
  // Text statistics
  onShowTextStats?: () => void;
  // Read-only mode
  readOnly?: boolean;
  // Zoom
  zoomLevel?: number;
  onZoomChange?: (level: number) => void;
  // Find & Replace
  onFindReplace?: () => void;
  // Link
  onInsertLink?: () => void;
  // Image upload — when provided, images inserted via toolbar are uploaded to
  // GCS in the background after an instant base64 preview.
  onImageUpload?: (file: File) => Promise<{ src: string; gcsFileName: string }>;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className={`tiptap-toolbar-btn ${isActive ? "active" : ""}`}
  >
    {children}
  </button>
);

const ToolbarDivider: React.FC = () => (
  <div className="tiptap-toolbar-divider" />
);

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  editor,
  onExportDocx,
  onImportDocx,
  // Save
  onSave,
  isSaving,
  showUnsavedBadge,
  // Track changes
  trackChangesEnabled,
  onToggleTrackChanges,
  trackChanges = [],
  currentChangeIndex = 0,
  onNavigateChange,
  onAcceptChange,
  onRejectChange,
  onAcceptAllChanges,
  onRejectAllChanges,
  // Comments panel
  showCommentsPanel,
  onToggleCommentsPanel,
  commentsCount = 0,
  onAddComment,
  // Track changes panel
  showTrackChangesPanel,
  onToggleTrackChangesPanel,
  trackChangesCount = 0,
  // Text statistics
  onShowTextStats,
  // Read-only mode
  readOnly,
  // Zoom
  zoomLevel = 100,
  onZoomChange,
  // Find & Replace
  onFindReplace,
  // Link
  onInsertLink,
  // Image upload
  onImageUpload,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Force re-render on selection/transaction changes so font, size, and
  // line-height dropdowns reflect the current cursor position.
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick(n => n + 1);
    editor.on("selectionUpdate", bump);
    editor.on("transaction", bump);
    return () => {
      editor.off("selectionUpdate", bump);
      editor.off("transaction", bump);
    };
  }, [editor]);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [showCustomTable, setShowCustomTable] = useState(false);
  const [customRows, setCustomRows] = useState(3);
  const [customCols, setCustomCols] = useState(3);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showAlignmentPicker, setShowAlignmentPicker] = useState(false);
  const [showHeadingPicker, setShowHeadingPicker] = useState(false);
  const [showInsertPicker, setShowInsertPicker] = useState(false);
  const [showMathModal, setShowMathModal] = useState(false);
  const [showZoomPicker, setShowZoomPicker] = useState(false);
  const [showSpacingPicker, setShowSpacingPicker] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const [firstHiddenGroup, setFirstHiddenGroup] = useState<number>(Infinity);
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);
  const zoomPickerRef = useRef<HTMLDivElement>(null);

  // Detect which toolbar groups overflow the collapsible container
  useEffect(() => {
    const container = collapsibleRef.current;
    if (!container) return;
    const checkOverflow = () => {
      const containerWidth = container.clientWidth;
      const groups = container.querySelectorAll<HTMLElement>(
        "[data-toolbar-group]"
      );
      let firstHidden = Infinity;
      groups.forEach(group => {
        const idx = parseInt(group.dataset.toolbarGroup || "0", 10);
        const rightEdge = group.offsetLeft + group.offsetWidth;
        if (rightEdge > containerWidth + 2 && idx < firstHidden) {
          firstHidden = idx;
        }
      });
      setFirstHiddenGroup(firstHidden);
      if (firstHidden === Infinity) setShowOverflow(false);
    };
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(container);
    checkOverflow();
    return () => observer.disconnect();
  }, []);

  // Close zoom picker when clicking outside
  useEffect(() => {
    if (!showZoomPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        zoomPickerRef.current &&
        !zoomPickerRef.current.contains(e.target as Node)
      ) {
        setShowZoomPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showZoomPicker]);

  // Close overflow popup when clicking outside
  useEffect(() => {
    if (!showOverflow) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        overflowRef.current &&
        !overflowRef.current.contains(e.target as Node)
      ) {
        setShowOverflow(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOverflow]);

  // Close any open toolbar dropdown picker (color, highlight, heading,
  // spacing, alignment, insert, table) when clicking outside its dropdown.
  useEffect(() => {
    const anyPickerOpen =
      showColorPicker ||
      showHighlightPicker ||
      showAlignmentPicker ||
      showHeadingPicker ||
      showInsertPicker ||
      showSpacingPicker ||
      showTablePicker;
    if (!anyPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest(".tiptap-toolbar-dropdown")) return;
      setShowColorPicker(false);
      setShowHighlightPicker(false);
      setShowAlignmentPicker(false);
      setShowHeadingPicker(false);
      setShowInsertPicker(false);
      setShowSpacingPicker(false);
      setShowTablePicker(false);
      setShowCustomTable(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [
    showColorPicker,
    showHighlightPicker,
    showAlignmentPicker,
    showHeadingPicker,
    showInsertPicker,
    showSpacingPicker,
    showTablePicker,
  ]);

  // Get current change for accept/reject
  const currentChange = trackChanges[currentChangeIndex];

  const handleFontFamilyChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      if (value) {
        editor.chain().focus().setFontFamily(value).run();
      } else {
        editor.chain().focus().unsetFontFamily().run();
      }
    },
    [editor]
  );

  const handleFontSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      if (value) {
        (editor.chain().focus() as any).setFontSize(value).run();
      } else {
        (editor.chain().focus() as any).unsetFontSize().run();
      }
    },
    [editor]
  );

  const handleImageUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async e => {
          const base64 = e.target?.result;
          if (typeof base64 !== "string") return;
          // 1. Instant preview with base64
          editor.chain().focus().setImage({ src: base64 }).run();
          // 2. Background upload → swap src + set gcsFileName
          if (onImageUpload) {
            try {
              const { src: proxyUrl, gcsFileName } = await onImageUpload(file);
              // Use TipTap's chain API to build a single transaction
              editor
                .chain()
                .command(({ tr }) => {
                  let found = false;
                  tr.doc.descendants((node, pos) => {
                    if (
                      !found &&
                      node.type.name === "image" &&
                      node.attrs.src === base64
                    ) {
                      tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        src: proxyUrl,
                        gcsFileName,
                      });
                      found = true;
                    }
                  });
                  return found;
                })
                .run();
            } catch (err) {
              console.error("[EditorToolbar] Image upload failed:", err);
              // Revert: remove the orphaned base64 image from the document
              editor
                .chain()
                .command(({ tr }) => {
                  let found = false;
                  tr.doc.descendants((node, pos) => {
                    if (
                      !found &&
                      node.type.name === "image" &&
                      node.attrs.src === base64
                    ) {
                      tr.delete(pos, pos + node.nodeSize);
                      found = true;
                    }
                  });
                  return found;
                })
                .run();
            }
          }
        };
        reader.readAsDataURL(file);
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [editor, onImageUpload]
  );

  const handleInsertTable = useCallback(
    (rows: number, cols: number) => {
      editor
        .chain()
        .focus()
        .insertTable({ rows, cols, withHeaderRow: true })
        .run();
      setShowTablePicker(false);
      setShowCustomTable(false);
    },
    [editor]
  );

  const handleCustomTableSubmit = useCallback(() => {
    if (customRows > 0 && customCols > 0) {
      handleInsertTable(customRows, customCols);
    }
  }, [customRows, customCols, handleInsertTable]);

  const handleTextColorChange = useCallback(
    (color: string) => {
      if (color) {
        editor.chain().focus().setColor(color).run();
      } else {
        editor.chain().focus().unsetColor().run();
      }
      setShowColorPicker(false);
    },
    [editor]
  );

  const handleHighlightChange = useCallback(
    (color: string) => {
      if (color) {
        editor.chain().focus().setBackgroundColor(color).run();
      } else {
        editor.chain().focus().unsetBackgroundColor().run();
      }
      setShowHighlightPicker(false);
    },
    [editor]
  );

  const handleZoomFit = useCallback(() => {
    if (!onZoomChange) return;
    const container = document.querySelector(".tiptap-editor-content");
    if (container) {
      const containerWidth = container.clientWidth;
      const pageWidth = 816; // 8.5" at 96dpi
      const padding = 40; // leave some breathing room
      const fitLevel = Math.round(
        ((containerWidth - padding) / pageWidth) * 100
      );
      onZoomChange(Math.min(100, Math.max(50, fitLevel)));
    }
    setShowZoomPicker(false);
  }, [onZoomChange]);

  const handleInsertMath = useCallback(
    (latex: string, isBlock: boolean) => {
      editor
        .chain()
        .focus()
        .insertContent({
          type: isBlock ? "blockMath" : "inlineMath",
          attrs: { latex },
        })
        .run();
    },
    [editor]
  );

  const renderOverflowContent = () => {
    const rows: React.ReactNode[] = [];
    if (firstHiddenGroup <= 0) {
      rows.push(
        <div key="of-0" className="tiptap-toolbar-overflow-row">
          {onSave && (
            <ToolbarButton
              onClick={onSave}
              disabled={isSaving || readOnly}
              title={t("tiptapEditor.toolbar.saveTooltip")}
            >
              <FiSave />
            </ToolbarButton>
          )}
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title={t("tiptapEditor.toolbar.undoTooltip")}
          >
            <BsArrowCounterclockwise />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title={t("tiptapEditor.toolbar.redoTooltip")}
          >
            <BsArrowClockwise />
          </ToolbarButton>
        </div>
      );
    }
    if (firstHiddenGroup <= 2) {
      rows.push(
        <div key="of-2" className="tiptap-toolbar-overflow-row">
          <select
            className="tiptap-toolbar-select"
            onChange={handleFontFamilyChange}
            value={resolveDropdownFont(
              editor.getAttributes("textStyle").fontFamily
            )}
            title={t("tiptapEditor.toolbar.fontFamily")}
          >
            {renderFontOptions(
              resolveDropdownFont(editor.getAttributes("textStyle").fontFamily)
            )}
          </select>
          <select
            className="tiptap-toolbar-select"
            onChange={handleFontSizeChange}
            value={editor.getAttributes("textStyle").fontSize || ""}
            title={t("tiptapEditor.toolbar.fontSize")}
          >
            {renderFontSizeOptions(
              editor.getAttributes("textStyle").fontSize || ""
            )}
          </select>
        </div>
      );
    }
    if (firstHiddenGroup <= 3) {
      rows.push(
        <div key="of-3" className="tiptap-toolbar-overflow-row">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive("bold")}
            title={t("tiptapEditor.toolbar.boldTooltip")}
          >
            <BsTypeBold />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive("italic")}
            title={t("tiptapEditor.toolbar.italicTooltip")}
          >
            <BsTypeItalic />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive("underline")}
            title={t("tiptapEditor.toolbar.underlineTooltip")}
          >
            <BsTypeUnderline />
          </ToolbarButton>
          {onInsertLink && (
            <ToolbarButton
              onClick={onInsertLink}
              isActive={editor.isActive("link")}
              title={t("tiptapEditor.toolbar.insertLinkTooltip")}
              disabled={readOnly}
            >
              <BsLink45Deg />
            </ToolbarButton>
          )}
          <ToolbarButton
            onClick={() => {
              setShowColorPicker(!showColorPicker);
              setShowHighlightPicker(false);
              setShowOverflow(true);
            }}
            isActive={showColorPicker}
            title={t("tiptapEditor.toolbar.textColor")}
          >
            <AiOutlineFontColors />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              setShowHighlightPicker(!showHighlightPicker);
              setShowColorPicker(false);
              setShowOverflow(true);
            }}
            isActive={showHighlightPicker}
            title={t("tiptapEditor.toolbar.highlightColor")}
          >
            <BsHighlighter />
          </ToolbarButton>
        </div>
      );
    }
    if (firstHiddenGroup <= 4) {
      rows.push(
        <div key="of-4" className="tiptap-toolbar-overflow-row">
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            isActive={editor.isActive("heading", { level: 1 })}
            title={t("tiptapEditor.formatMenu.heading1")}
          >
            <LuHeading1 />
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            isActive={editor.isActive("heading", { level: 2 })}
            title={t("tiptapEditor.formatMenu.heading2")}
          >
            <LuHeading2 />
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            isActive={editor.isActive("heading", { level: 3 })}
            title={t("tiptapEditor.formatMenu.heading3")}
          >
            <LuHeading3 />
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 4 }).run()
            }
            isActive={editor.isActive("heading", { level: 4 })}
            title={t("tiptapEditor.formatMenu.heading4")}
          >
            <LuHeading4 />
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 5 }).run()
            }
            isActive={editor.isActive("heading", { level: 5 })}
            title={t("tiptapEditor.formatMenu.heading5")}
          >
            <LuHeading5 />
          </ToolbarButton>
          <ToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 6 }).run()
            }
            isActive={editor.isActive("heading", { level: 6 })}
            title={t("tiptapEditor.formatMenu.heading6")}
          >
            <LuHeading6 />
          </ToolbarButton>
        </div>
      );
    }
    if (firstHiddenGroup <= 5) {
      rows.push(
        <div key="of-5" className="tiptap-toolbar-overflow-row">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive("bulletList")}
            title={t("tiptapEditor.toolbar.bulletListTooltip")}
          >
            <BsListUl />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive("orderedList")}
            title={t("tiptapEditor.toolbar.numberedListTooltip")}
          >
            <BsListOl />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().outdent().run()}
            title={t("tiptapEditor.toolbar.decreaseIndentTooltip")}
            disabled={readOnly}
          >
            <BsTextIndentRight />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().indent().run()}
            title={t("tiptapEditor.toolbar.increaseIndentTooltip")}
            disabled={readOnly}
          >
            <BsTextIndentLeft />
          </ToolbarButton>
        </div>
      );
    }
    if (firstHiddenGroup <= 6) {
      rows.push(
        <div key="of-6" className="tiptap-toolbar-overflow-row">
          {/* Line Spacing */}
          {[
            { label: t("tiptapEditor.toolbar.single"), value: "1" },
            { label: "1.15", value: "1.15" },
            { label: "1.5", value: "1.5" },
            { label: t("tiptapEditor.toolbar.double"), value: "2" },
          ].map(opt => (
            <ToolbarButton
              key={`ls-${opt.value}`}
              onClick={() => {
                editor.chain().focus().setLineHeight(opt.value).run();
                setShowOverflow(false);
              }}
              isActive={
                editor.getAttributes("paragraph").lineHeight === opt.value
              }
              title={`${t("tiptapEditor.toolbar.lineSpacingTooltip")}: ${opt.label}`}
            >
              <span style={{ fontSize: "10px", fontWeight: 500 }}>
                {opt.label}
              </span>
            </ToolbarButton>
          ))}
          <span
            style={{
              borderLeft: "1px solid var(--toolbar-divider, #ddd)",
              margin: "0 4px",
              height: "20px",
            }}
          />
          {/* Alignment */}
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().setTextAlign("left").run();
              setShowOverflow(false);
            }}
            isActive={editor.isActive({ textAlign: "left" })}
            title={t("tiptapEditor.toolbar.alignLeftTooltip")}
          >
            <BsTextLeft />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().setTextAlign("center").run();
              setShowOverflow(false);
            }}
            isActive={editor.isActive({ textAlign: "center" })}
            title={t("tiptapEditor.toolbar.alignCenterTooltip")}
          >
            <BsTextCenter />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().setTextAlign("right").run();
              setShowOverflow(false);
            }}
            isActive={editor.isActive({ textAlign: "right" })}
            title={t("tiptapEditor.toolbar.alignRightTooltip")}
          >
            <BsTextRight />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().setTextAlign("justify").run();
              setShowOverflow(false);
            }}
            isActive={editor.isActive({ textAlign: "justify" })}
            title={t("tiptapEditor.toolbar.justifyTooltip")}
          >
            <BsJustify />
          </ToolbarButton>
          <span
            style={{
              borderLeft: "1px solid var(--toolbar-divider, #ddd)",
              margin: "0 4px",
              height: "20px",
            }}
          />
          {/* Insert items */}
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().toggleBlockquote().run();
              setShowOverflow(false);
            }}
            isActive={editor.isActive("blockquote")}
            title={t("tiptapEditor.toolbar.blockquoteTooltip")}
          >
            <BsBlockquoteLeft />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().toggleCodeBlock().run();
              setShowOverflow(false);
            }}
            isActive={editor.isActive("codeBlock")}
            title={t("tiptapEditor.toolbar.codeBlockTooltip")}
          >
            <BsCode />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              editor.chain().focus().setHorizontalRule().run();
              setShowOverflow(false);
            }}
            title={t("tiptapEditor.insertMenu.horizontalRule")}
          >
            <VscHorizontalRule />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              fileInputRef.current?.click();
              setShowOverflow(false);
            }}
            title={t("tiptapEditor.insertMenu.insertImage")}
          >
            <BsImage />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              setShowMathModal(true);
              setShowOverflow(false);
            }}
            title={t("tiptapEditor.insertMenu.insertMathFormula")}
          >
            <TbMathFunction />
          </ToolbarButton>
          {onFindReplace && (
            <>
              <span
                style={{
                  borderLeft: "1px solid var(--toolbar-divider, #ddd)",
                  margin: "0 4px",
                  height: "20px",
                }}
              />
              <ToolbarButton
                onClick={() => {
                  onFindReplace();
                  setShowOverflow(false);
                }}
                title={t("tiptapEditor.toolbar.findReplaceTooltip")}
              >
                <BsSearch />
              </ToolbarButton>
            </>
          )}
        </div>
      );
    }
    return rows;
  };

  return (
    <div className="tiptap-toolbar" ref={toolbarRef}>
      <div className="tiptap-toolbar-collapsible" ref={collapsibleRef}>
        {/* Save & History (Undo/Redo) */}
        <div className="tiptap-toolbar-group" data-toolbar-group="0">
          {onSave && (
            <ToolbarButton
              onClick={onSave}
              disabled={isSaving || readOnly}
              title={
                isSaving
                  ? t("tiptapEditor.toolbar.savingTooltip")
                  : showUnsavedBadge
                    ? t("tiptapEditor.toolbar.saveUnsaved")
                    : t("tiptapEditor.toolbar.saveTooltip")
              }
            >
              <FiSave />
            </ToolbarButton>
          )}
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title={t("tiptapEditor.toolbar.undoTooltip")}
          >
            <BsArrowCounterclockwise />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title={t("tiptapEditor.toolbar.redoTooltip")}
          >
            <BsArrowClockwise />
          </ToolbarButton>
        </div>

        <ToolbarDivider />

        {/* Zoom Dropdown */}
        {onZoomChange && (
          <>
            <div
              className="tiptap-toolbar-group"
              data-toolbar-group="1"
              ref={zoomPickerRef}
            >
              <div className="tiptap-toolbar-dropdown">
                <button
                  type="button"
                  className={`tiptap-zoom-btn ${showZoomPicker ? "active" : ""}`}
                  onClick={() => {
                    setShowZoomPicker(!showZoomPicker);
                    setShowColorPicker(false);
                    setShowHighlightPicker(false);
                    setShowTablePicker(false);
                    setShowAlignmentPicker(false);
                    setShowHeadingPicker(false);
                    setShowInsertPicker(false);
                  }}
                  title={t("tiptapEditor.toolbar.zoom")}
                >
                  {zoomLevel}%<span className="tiptap-dropdown-arrow">▾</span>
                </button>
                {showZoomPicker && (
                  <div className="tiptap-zoom-picker">
                    <button
                      className="tiptap-zoom-option"
                      onClick={handleZoomFit}
                    >
                      {t("tiptapEditor.toolbar.fit")}
                    </button>
                    <div className="tiptap-zoom-divider" />
                    {[50, 75, 90, 100, 110, 125, 150, 200].map(level => (
                      <button
                        key={level}
                        className={`tiptap-zoom-option ${zoomLevel === level ? "active" : ""}`}
                        onClick={() => {
                          onZoomChange(level);
                          setShowZoomPicker(false);
                        }}
                      >
                        {level}%
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <ToolbarDivider />
          </>
        )}

        {/* Font Family & Size */}
        <div className="tiptap-toolbar-group" data-toolbar-group="2">
          <select
            className="tiptap-toolbar-select"
            onChange={handleFontFamilyChange}
            value={resolveDropdownFont(
              editor.getAttributes("textStyle").fontFamily
            )}
            title={t("tiptapEditor.toolbar.fontFamily")}
          >
            {renderFontOptions(
              resolveDropdownFont(editor.getAttributes("textStyle").fontFamily)
            )}
          </select>
          <select
            className="tiptap-toolbar-select"
            onChange={handleFontSizeChange}
            value={editor.getAttributes("textStyle").fontSize || ""}
            title={t("tiptapEditor.toolbar.fontSize")}
          >
            {renderFontSizeOptions(
              editor.getAttributes("textStyle").fontSize || ""
            )}
          </select>
        </div>

        <ToolbarDivider />

        {/* Text Formatting */}
        <div className="tiptap-toolbar-group" data-toolbar-group="3">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive("bold")}
            title={t("tiptapEditor.toolbar.boldTooltip")}
          >
            <BsTypeBold />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive("italic")}
            title={t("tiptapEditor.toolbar.italicTooltip")}
          >
            <BsTypeItalic />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive("underline")}
            title={t("tiptapEditor.toolbar.underlineTooltip")}
          >
            <BsTypeUnderline />
          </ToolbarButton>
          {onInsertLink && (
            <ToolbarButton
              onClick={onInsertLink}
              isActive={editor.isActive("link")}
              title={t("tiptapEditor.toolbar.insertLinkTooltip")}
              disabled={readOnly}
            >
              <BsLink45Deg />
            </ToolbarButton>
          )}
          <div className="tiptap-toolbar-dropdown">
            <ToolbarButton
              onClick={() => {
                setShowColorPicker(!showColorPicker);
                setShowHighlightPicker(false);
                setShowTablePicker(false);
                setShowAlignmentPicker(false);
                setShowHeadingPicker(false);
                setShowInsertPicker(false);
                setShowZoomPicker(false);
              }}
              isActive={showColorPicker}
              title={t("tiptapEditor.toolbar.textColor")}
            >
              <AiOutlineFontColors />
            </ToolbarButton>
            {showColorPicker && (
              <div className="tiptap-color-picker">
                {TEXT_COLORS.map(color => (
                  <button
                    key={color.value || "default"}
                    className="tiptap-color-swatch"
                    style={{
                      backgroundColor: color.value || "#ffffff",
                      border: color.value ? "none" : "1px solid #ccc",
                    }}
                    onClick={() => handleTextColorChange(color.value)}
                    title={color.label}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="tiptap-toolbar-dropdown">
            <ToolbarButton
              onClick={() => {
                setShowHighlightPicker(!showHighlightPicker);
                setShowColorPicker(false);
                setShowTablePicker(false);
                setShowAlignmentPicker(false);
                setShowHeadingPicker(false);
                setShowInsertPicker(false);
                setShowZoomPicker(false);
              }}
              isActive={showHighlightPicker}
              title={t("tiptapEditor.toolbar.highlightColor")}
            >
              <BsHighlighter />
            </ToolbarButton>
            {showHighlightPicker && (
              <div className="tiptap-color-picker">
                {HIGHLIGHT_COLORS.map(color => (
                  <button
                    key={color.value || "none"}
                    className="tiptap-color-swatch"
                    style={{
                      backgroundColor: color.value || "#ffffff",
                      border: color.value ? "none" : "1px solid #ccc",
                    }}
                    onClick={() => handleHighlightChange(color.value)}
                    title={color.label}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <ToolbarDivider />

        {/* Headings - Dropdown */}
        <div className="tiptap-toolbar-group" data-toolbar-group="4">
          <div className="tiptap-toolbar-dropdown">
            <ToolbarButton
              onClick={() => {
                setShowHeadingPicker(!showHeadingPicker);
                setShowColorPicker(false);
                setShowHighlightPicker(false);
                setShowTablePicker(false);
                setShowAlignmentPicker(false);
                setShowInsertPicker(false);
                setShowZoomPicker(false);
              }}
              isActive={showHeadingPicker || editor.isActive("heading")}
              title={t("tiptapEditor.toolbar.headingsTooltip")}
            >
              {editor.isActive("heading", { level: 2 }) ? (
                <LuHeading2 />
              ) : editor.isActive("heading", { level: 3 }) ? (
                <LuHeading3 />
              ) : editor.isActive("heading", { level: 4 }) ? (
                <LuHeading4 />
              ) : editor.isActive("heading", { level: 5 }) ? (
                <LuHeading5 />
              ) : editor.isActive("heading", { level: 6 }) ? (
                <LuHeading6 />
              ) : (
                <LuHeading1 />
              )}
              <span className="tiptap-dropdown-arrow">▾</span>
            </ToolbarButton>
            {showHeadingPicker && (
              <div className="tiptap-heading-picker">
                <button
                  className={`tiptap-heading-btn ${editor.isActive("heading", { level: 1 }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: 1 }).run();
                    setShowHeadingPicker(false);
                  }}
                  title={t("tiptapEditor.formatMenu.heading1")}
                >
                  <LuHeading1 />
                </button>
                <button
                  className={`tiptap-heading-btn ${editor.isActive("heading", { level: 2 }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: 2 }).run();
                    setShowHeadingPicker(false);
                  }}
                  title={t("tiptapEditor.formatMenu.heading2")}
                >
                  <LuHeading2 />
                </button>
                <button
                  className={`tiptap-heading-btn ${editor.isActive("heading", { level: 3 }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: 3 }).run();
                    setShowHeadingPicker(false);
                  }}
                  title={t("tiptapEditor.formatMenu.heading3")}
                >
                  <LuHeading3 />
                </button>
                <button
                  className={`tiptap-heading-btn ${editor.isActive("heading", { level: 4 }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: 4 }).run();
                    setShowHeadingPicker(false);
                  }}
                  title={t("tiptapEditor.formatMenu.heading4")}
                >
                  <LuHeading4 />
                </button>
                <button
                  className={`tiptap-heading-btn ${editor.isActive("heading", { level: 5 }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: 5 }).run();
                    setShowHeadingPicker(false);
                  }}
                  title={t("tiptapEditor.formatMenu.heading5")}
                >
                  <LuHeading5 />
                </button>
                <button
                  className={`tiptap-heading-btn ${editor.isActive("heading", { level: 6 }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: 6 }).run();
                    setShowHeadingPicker(false);
                  }}
                  title={t("tiptapEditor.formatMenu.heading6")}
                >
                  <LuHeading6 />
                </button>
              </div>
            )}
          </div>
        </div>

        <ToolbarDivider />

        {/* Lists */}
        <div className="tiptap-toolbar-group" data-toolbar-group="5">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive("bulletList")}
            title={t("tiptapEditor.toolbar.bulletListTooltip")}
          >
            <BsListUl />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive("orderedList")}
            title={t("tiptapEditor.toolbar.numberedListTooltip")}
          >
            <BsListOl />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().outdent().run()}
            title={t("tiptapEditor.toolbar.decreaseIndentTooltip")}
            disabled={readOnly}
          >
            <BsTextIndentRight />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().indent().run()}
            title={t("tiptapEditor.toolbar.increaseIndentTooltip")}
            disabled={readOnly}
          >
            <BsTextIndentLeft />
          </ToolbarButton>
        </div>

        <ToolbarDivider />

        {/* Line Spacing Dropdown */}
        <div className="tiptap-toolbar-group" data-toolbar-group="6">
          <div className="tiptap-toolbar-dropdown">
            <ToolbarButton
              onClick={() => {
                setShowSpacingPicker(!showSpacingPicker);
                setShowColorPicker(false);
                setShowHighlightPicker(false);
                setShowTablePicker(false);
                setShowAlignmentPicker(false);
                setShowHeadingPicker(false);
                setShowInsertPicker(false);
                setShowZoomPicker(false);
              }}
              isActive={showSpacingPicker}
              title={t("tiptapEditor.toolbar.lineSpacingTooltip")}
            >
              <BsDistributeVertical />
              <span className="tiptap-dropdown-arrow">▾</span>
            </ToolbarButton>
            {showSpacingPicker && (
              <div className="tiptap-spacing-picker">
                {[
                  { label: t("tiptapEditor.toolbar.single"), value: "1" },
                  { label: "1.15", value: "1.15" },
                  { label: "1.5", value: "1.5" },
                  { label: t("tiptapEditor.toolbar.double"), value: "2" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    className={`tiptap-spacing-option ${
                      editor.getAttributes("paragraph").lineHeight === opt.value
                        ? "active"
                        : ""
                    }`}
                    onClick={() => {
                      editor.chain().focus().setLineHeight(opt.value).run();
                      setShowSpacingPicker(false);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="tiptap-toolbar-dropdown">
            <ToolbarButton
              onClick={() => {
                setShowAlignmentPicker(!showAlignmentPicker);
                setShowColorPicker(false);
                setShowHighlightPicker(false);
                setShowTablePicker(false);
                setShowHeadingPicker(false);
                setShowInsertPicker(false);
                setShowZoomPicker(false);
              }}
              isActive={showAlignmentPicker}
              title={t("tiptapEditor.toolbar.alignmentTooltip")}
            >
              <RiAlignLeft />
              <span className="tiptap-dropdown-arrow">▾</span>
            </ToolbarButton>
            {showAlignmentPicker && (
              <div className="tiptap-alignment-picker">
                <button
                  className={`tiptap-alignment-btn ${editor.isActive({ textAlign: "left" }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().setTextAlign("left").run();
                    setShowAlignmentPicker(false);
                  }}
                  title={t("tiptapEditor.toolbar.alignLeftTooltip")}
                >
                  <BsTextLeft />
                </button>
                <button
                  className={`tiptap-alignment-btn ${editor.isActive({ textAlign: "center" }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().setTextAlign("center").run();
                    setShowAlignmentPicker(false);
                  }}
                  title={t("tiptapEditor.toolbar.alignCenterTooltip")}
                >
                  <BsTextCenter />
                </button>
                <button
                  className={`tiptap-alignment-btn ${editor.isActive({ textAlign: "right" }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().setTextAlign("right").run();
                    setShowAlignmentPicker(false);
                  }}
                  title={t("tiptapEditor.toolbar.alignRightTooltip")}
                >
                  <BsTextRight />
                </button>
                <button
                  className={`tiptap-alignment-btn ${editor.isActive({ textAlign: "justify" }) ? "active" : ""}`}
                  onClick={() => {
                    editor.chain().focus().setTextAlign("justify").run();
                    setShowAlignmentPicker(false);
                  }}
                  title={t("tiptapEditor.toolbar.justifyTooltip")}
                >
                  <BsJustify />
                </button>
              </div>
            )}
          </div>
          <div className="tiptap-toolbar-dropdown">
            <ToolbarButton
              onClick={() => {
                setShowInsertPicker(!showInsertPicker);
                setShowTablePicker(false);
                setShowCustomTable(false);
                setShowColorPicker(false);
                setShowHighlightPicker(false);
                setShowAlignmentPicker(false);
                setShowHeadingPicker(false);
                setShowZoomPicker(false);
              }}
              isActive={showInsertPicker}
              title={t("tiptapEditor.toolbar.insertTooltip")}
            >
              <BsPlusLg />
              <span className="tiptap-dropdown-arrow">▾</span>
            </ToolbarButton>
            {showInsertPicker && (
              <div className="tiptap-insert-picker">
                {/* First row: Blockquote and Code Block as icon buttons */}
                <div className="tiptap-insert-icon-row">
                  <button
                    className={`tiptap-insert-icon-btn ${editor.isActive("blockquote") ? "active" : ""}`}
                    onClick={() => {
                      editor.chain().focus().toggleBlockquote().run();
                      setShowInsertPicker(false);
                    }}
                    title={t("tiptapEditor.toolbar.blockquoteTooltip")}
                  >
                    <BsBlockquoteLeft />
                  </button>
                  <button
                    className={`tiptap-insert-icon-btn ${editor.isActive("codeBlock") ? "active" : ""}`}
                    onClick={() => {
                      editor.chain().focus().toggleCodeBlock().run();
                      setShowInsertPicker(false);
                    }}
                    title={t("tiptapEditor.toolbar.codeBlockTooltip")}
                  >
                    <BsCode />
                  </button>
                </div>
                <div className="tiptap-insert-divider" />
                {/* Horizontal Rule */}
                <button
                  className="tiptap-insert-btn"
                  onClick={() => {
                    editor.chain().focus().setHorizontalRule().run();
                    setShowInsertPicker(false);
                  }}
                  title={t("tiptapEditor.insertMenu.horizontalRule")}
                >
                  <VscHorizontalRule />
                  <span>{t("tiptapEditor.insertMenu.horizontalRule")}</span>
                </button>
                {/* Table option */}
                <div className="tiptap-insert-option-wrapper">
                  <button
                    className={`tiptap-insert-btn ${showTablePicker ? "active" : ""}`}
                    onClick={() => setShowTablePicker(!showTablePicker)}
                    title={t("tiptapEditor.insertMenu.insertTable")}
                  >
                    <BsTable />
                    <span>{t("tiptapEditor.insertMenu.table")}</span>
                  </button>
                  {showTablePicker && (
                    <div className="tiptap-table-picker-submenu">
                      {!showCustomTable ? (
                        <>
                          <div className="tiptap-table-picker-label">
                            {t("tiptapEditor.insertMenu.selectTableSize")}
                          </div>
                          <div className="tiptap-table-picker-grid">
                            {[2, 3, 4, 5].map(rows =>
                              [2, 3, 4, 5].map(cols => (
                                <button
                                  key={`${rows}x${cols}`}
                                  className="tiptap-table-size-btn"
                                  onClick={() => {
                                    handleInsertTable(rows, cols);
                                    setShowInsertPicker(false);
                                  }}
                                  title={`${rows} × ${cols}`}
                                >
                                  {rows}×{cols}
                                </button>
                              ))
                            )}
                          </div>
                          <div className="tiptap-table-picker-divider" />
                          <button
                            className="tiptap-table-custom-btn"
                            onClick={() => setShowCustomTable(true)}
                          >
                            {t("tiptapEditor.insertMenu.customSize")}
                          </button>
                        </>
                      ) : (
                        <div className="tiptap-table-custom-form">
                          <div className="tiptap-table-picker-label">
                            {t("tiptapEditor.insertMenu.customTableSize")}
                          </div>
                          <div className="tiptap-table-custom-inputs">
                            <div className="tiptap-table-custom-field">
                              <label>{t("tiptapEditor.insertMenu.rows")}</label>
                              <input
                                type="number"
                                min="1"
                                max="50"
                                value={customRows}
                                onChange={e =>
                                  setCustomRows(
                                    Math.max(1, parseInt(e.target.value) || 1)
                                  )
                                }
                              />
                            </div>
                            <span className="tiptap-table-custom-x">×</span>
                            <div className="tiptap-table-custom-field">
                              <label>{t("tiptapEditor.insertMenu.cols")}</label>
                              <input
                                type="number"
                                min="1"
                                max="20"
                                value={customCols}
                                onChange={e =>
                                  setCustomCols(
                                    Math.max(1, parseInt(e.target.value) || 1)
                                  )
                                }
                              />
                            </div>
                          </div>
                          <div className="tiptap-table-custom-actions">
                            <button
                              className="tiptap-table-custom-cancel"
                              onClick={() => setShowCustomTable(false)}
                            >
                              {t("tiptapEditor.insertMenu.back")}
                            </button>
                            <button
                              className="tiptap-table-custom-submit"
                              onClick={() => {
                                handleCustomTableSubmit();
                                setShowInsertPicker(false);
                              }}
                            >
                              {t("tiptapEditor.insertMenu.insert")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Image option */}
                <button
                  className="tiptap-insert-btn"
                  onClick={() => {
                    fileInputRef.current?.click();
                    setShowInsertPicker(false);
                  }}
                  title={t("tiptapEditor.insertMenu.insertImage")}
                >
                  <BsImage />
                  <span>{t("tiptapEditor.insertMenu.image")}</span>
                </button>
                {/* Formula option */}
                <button
                  className="tiptap-insert-btn"
                  onClick={() => {
                    setShowMathModal(true);
                    setShowInsertPicker(false);
                  }}
                  title={t("tiptapEditor.insertMenu.insertMathFormula")}
                >
                  <TbMathFunction />
                  <span>{t("tiptapEditor.insertMenu.formula")}</span>
                </button>
              </div>
            )}
          </div>
          {onFindReplace && (
            <ToolbarButton
              onClick={onFindReplace}
              title={t("tiptapEditor.toolbar.findReplaceTooltip")}
            >
              <BsSearch />
            </ToolbarButton>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: "none" }}
          />
        </div>
      </div>
      {/* end collapsible */}

      {/* Overflow menu for hidden toolbar groups */}
      {firstHiddenGroup < Infinity && (
        <div className="tiptap-toolbar-overflow" ref={overflowRef}>
          <ToolbarButton
            onClick={() => setShowOverflow(!showOverflow)}
            isActive={showOverflow}
            title={t("tiptapEditor.toolbar.moreFormatting")}
          >
            <BsThreeDotsVertical />
          </ToolbarButton>
          {showOverflow && (
            <div className="tiptap-toolbar-overflow-popup">
              {renderOverflowContent()}
            </div>
          )}
        </div>
      )}

      <div className="tiptap-toolbar-pinned">
        {/* Track Changes Section */}
        {onToggleTrackChanges && (
          <>
            <div className="tiptap-toolbar-group tiptap-track-changes-group">
              <ToolbarButton
                onClick={() => {
                  onToggleTrackChanges();
                }}
                isActive={trackChangesEnabled}
                title={
                  trackChangesEnabled
                    ? t("tiptapEditor.toolbar.disableTrackChanges")
                    : t("tiptapEditor.toolbar.enableTrackChanges")
                }
                disabled={readOnly}
              >
                <span className="tiptap-track-btn-text">
                  {trackChangesEnabled
                    ? t("tiptapEditor.toolbar.trackOn")
                    : t("tiptapEditor.toolbar.trackOff")}
                </span>
              </ToolbarButton>

              {trackChangesEnabled &&
                trackChanges.length > 0 &&
                onNavigateChange && (
                  <>
                    <ToolbarButton
                      onClick={() => onNavigateChange("prev")}
                      disabled={currentChangeIndex <= 0}
                      title={t("tiptapEditor.toolbar.previousChange")}
                    >
                      <BsChevronLeft />
                    </ToolbarButton>
                    <span
                      className="tiptap-change-counter"
                      title={t("tiptapEditor.toolbar.changeCounter")}
                    >
                      {currentChangeIndex + 1} / {trackChanges.length}
                    </span>
                    <ToolbarButton
                      onClick={() => onNavigateChange("next")}
                      disabled={currentChangeIndex >= trackChanges.length - 1}
                      title={t("tiptapEditor.toolbar.nextChange")}
                    >
                      <BsChevronRight />
                    </ToolbarButton>
                    {currentChange && onAcceptChange && (
                      <ToolbarButton
                        onClick={() => onAcceptChange(currentChange.id)}
                        title={t("tiptapEditor.toolbar.acceptCurrentChange")}
                        disabled={readOnly}
                      >
                        <BsCheck className="text-success" />
                      </ToolbarButton>
                    )}
                    {currentChange && onRejectChange && (
                      <ToolbarButton
                        onClick={() => onRejectChange(currentChange.id)}
                        title={t("tiptapEditor.toolbar.rejectCurrentChange")}
                        disabled={readOnly}
                      >
                        <BsX className="text-danger" />
                      </ToolbarButton>
                    )}
                    <span className="tiptap-toolbar-separator" />
                    {onAcceptAllChanges && (
                      <ToolbarButton
                        onClick={onAcceptAllChanges}
                        title={t("tiptapEditor.toolbar.acceptAllChanges")}
                        disabled={readOnly}
                      >
                        <span className="tiptap-all-btn text-success">
                          {t("tiptapEditor.toolbar.acceptAllLabel")}
                        </span>
                      </ToolbarButton>
                    )}
                    {onRejectAllChanges && (
                      <ToolbarButton
                        onClick={onRejectAllChanges}
                        title={t("tiptapEditor.toolbar.rejectAllChanges")}
                        disabled={readOnly}
                      >
                        <span className="tiptap-all-btn text-danger">
                          {t("tiptapEditor.toolbar.rejectAllLabel")}
                        </span>
                      </ToolbarButton>
                    )}
                  </>
                )}
            </div>
            <ToolbarDivider />
          </>
        )}

        {/* Add Comment button */}
        {onAddComment && (
          <div className="tiptap-toolbar-group">
            <button
              type="button"
              className="tiptap-toolbar-btn"
              title={t("tiptapEditor.toolbar.addComment", "Add Comment")}
              aria-label={t("tiptapEditor.toolbar.addComment", "Add Comment")}
              disabled={readOnly}
              onMouseDown={e => {
                // Prevent editor from losing focus/selection when clicking this button
                e.preventDefault();
              }}
              onClick={() => {
                onAddComment();
              }}
            >
              <BsChatDots />
              {commentsCount > 0 && (
                <span className="tiptap-toolbar-badge">{commentsCount}</span>
              )}
            </button>
          </div>
        )}
      </div>
      {/* end pinned */}

      {/* Math Input Modal */}
      <MathInputModal
        show={showMathModal}
        onHide={() => setShowMathModal(false)}
        onInsert={handleInsertMath}
      />
    </div>
  );
};

export default EditorToolbar;
